export const regexContainYoutubeLink =
  /((?:https?:)?\/\/)?((?:www)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/|shorts\/)?)([\w-]+)([^<>"\s)\]\\}]*)/g

export const youtubeUrlPattern =
  /^((?:https?:)?\/\/)?((?:www)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$/gm

export const isTextContainHtml = (text: string): boolean => {
  return /^<[^>]+>.*<\/[^>]+>$/gm.test(text)
}

/**
 * Formats plain text into rich text (HTML) format.
 *
 * This function performs the following operations:
 * 1. If text is empty, returns an empty string
 * 2. If text is not already HTML, wraps each non-empty line in <p> tags
 * 3. If text is HTML, replaces empty paragraphs with non-breaking space paragraphs
 * 4. Preserves existing anchor tags by temporarily replacing them with placeholders
 * 5. Converts YouTube links to clickable anchor tags with "youtube" as the display text
 * 6. Restores the original anchor tags that were temporarily replaced
 * 7. Ensures empty paragraphs have non-breaking spaces for proper rendering
 *
 * @param text - The input text to format
 * @returns The formatted rich text in HTML format
 */
export const formatTextToRichText = (text?: string, keeyYoutubeUrl?: boolean): string => {
  if (!text) return ""

  // Convert plain text to paragraphs or handle existing HTML
  const textWithParagraphs = isTextContainHtml(text)
    ? text.replace(/<p><\/p>/g, "<p>&nbsp;</p>")
    : text
        .split("\n")
        .map((p) => `<p>${p}</p>`)
        .join("")

  // Convert Markdown links [text](url) to HTML anchor tags
  const textWithMarkdownLinks = textWithParagraphs
    ? textWithParagraphs.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a target="_blank" href="$2">$1</a>')
    : ""

  // Find all anchor tags and temporarily replace them
  const anchorTags: string[] = []
  const textWithoutAnchorTags = textWithMarkdownLinks.replace(/<a[^>]*>.*?<\/a>/g, (match) => {
    anchorTags.push(match)
    return `<ANCHOR_TAG_${anchorTags.length - 1}>`
  })

  // Add anchor tags to youtube links
  const textWithNewAnchorTags = textWithoutAnchorTags.replace(
    regexContainYoutubeLink,
    keeyYoutubeUrl
      ? '<a target="_blank" rel="noopener noreferrer nofollow" href="$&">$&</a>'
      : '<a target="_blank" rel="noopener noreferrer nofollow" href="$&">youtube</a>',
  )

  // Restore previous anchor tags
  const textWithPreviousAnchorTags = textWithNewAnchorTags.replace(
    /<ANCHOR_TAG_(\d+)>/g,
    (_, index: string) => anchorTags[parseInt(index, 10)] ?? "",
  )

  const textWithYoutubeAnchorReplaced = textWithPreviousAnchorTags.replace(
    /<a([^>]+)href="([^"]*youtube\.com[^"]*|[^"]*youtu\.be[^"]*)"([^>]*)>(.*?)<\/a>/gi,
    (match, beforeHref, href, afterHref, innerText) => {
      // Replace the link text with "youtube" only if the link text is exactly the link itself
      if (innerText.trim() === href.trim()) {
        return `<a${beforeHref}href="${href}"${afterHref}>youtube</a>`
      }
      return match
    },
  )

  return textWithYoutubeAnchorReplaced
}

export const isRichTextLongerThan = ({
  richText,
  numberOfLines,
  maxLineLength,
}: {
  richText: string
  numberOfLines: number
  maxLineLength: number
}) => {
  if (!richText.includes("<p>")) {
    return richText.length > numberOfLines * maxLineLength
  }

  const paragraphs = richText
    .split("<p>")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

  if (paragraphs.length > numberOfLines) return true

  const totalLines = paragraphs.reduce((total, content) => {
    // Strip all HTML tags and normalize non-breaking spaces
    const plainText = content.replace(/<[^>]*>/g, "").replace(/&nbsp;|&#160;/gi, " ")
    const length = plainText.length
    return total + Math.max(1, Math.ceil(length / maxLineLength))
  }, 0)

  return totalLines > numberOfLines
}

export const isRichTextEmpty = (richText: string) => {
  if (richText.trim() === "") return true

  const plainText = richText.replace(/<[^>]*>/g, "").replace(/&nbsp;|&#160;/gi, " ")

  return plainText.trim() === ""
}

export const isYoutubeUrl = (word: string) => {
  return word.match(youtubeUrlPattern)
}

export const getYouTubeVideoIdFromUrl = (url: string) => {
  // Our regex pattern to look for a youTube ID
  const regExp = /(youtu.*be.*)\/(watch\?v=|embed\/|v|shorts|)(.*?((?=[&#?])|$))/gm
  //Match the url with the regex
  const match = regExp.exec(url)
  //Return the result
  return match && match[3] && match[3].length === 11 ? match[3] : undefined
}
